﻿import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  fetchTeamSubPrice,
  previewTeamPlanPrice,
  type TeamSubPrice,
} from '@/api/modules/team';
import { DEFAULT_TEAM_COUNTRY, teamCountryCurrency } from '@/constants/teamCountries';

const FALLBACK_PRICE: TeamSubPrice = {
  priceMonth: 39,
  priceYear: 468,
  basePackagePriceMonth: 39,
  basePackagePriceYear: 468,
  baseMemberCount: 3,
  extraMemberPriceMonth: 15,
  extraMemberPriceYear: 180,
  extraMembers: 0,
  teamCapacity: null,
};

const BILLING_POINTS = [
  {
    icon: 'ri-group-line',
    title: 'Included seats are in the base fee',
    desc: 'The plan price covers every seat up to the included team limit. Extra people beyond that limit are billed at the add-on seat rate.',
  },
  {
    icon: 'ri-calendar-schedule-line',
    title: 'Extra seats follow your billing cycle',
    desc: 'Add-on seats are charged monthly or yearly with the main plan. You can add or remove team members anytime.',
  },
  {
    icon: 'ri-flask-line',
    title: 'One free trial per account',
    desc: 'Claim a 1-month trial when you create a new team. It can only be used once.',
  },
  {
    icon: 'ri-arrow-up-circle-line',
    title: 'Upgrade anytime, prorated',
    desc: 'Move to a larger team plan whenever you need. Remaining-period charges are prorated.',
  },
];

function formatPrice(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

function yearlySavingsPercent(month: number, year: number): number | null {
  const fullYear = month * 12;
  if (fullYear <= 0 || year >= fullYear) return null;
  return Math.round(((fullYear - year) / fullYear) * 100);
}

export default function PricingSection() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [annual, setAnnual] = useState(false);
  const [price, setPrice] = useState<TeamSubPrice>(FALLBACK_PRICE);
  const [loading, setLoading] = useState(true);
  const [teamSize, setTeamSize] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const data = await fetchTeamSubPrice({ country: DEFAULT_TEAM_COUNTRY });
        if (!cancelled) setPrice(data);
      } catch {
        if (!cancelled) setPrice(FALLBACK_PRICE);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const included = Math.max(1, price.baseMemberCount || FALLBACK_PRICE.baseMemberCount);
  const size = teamSize ?? included;
  const extraCount = Math.max(0, size - included);
  const preview = previewTeamPlanPrice(price, extraCount);
  const displayAmount = annual ? preview.year : preview.month;
  const extraUnit = annual ? price.extraMemberPriceYear : price.extraMemberPriceMonth;
  const baseDisplay = annual ? price.basePackagePriceYear : price.basePackagePriceMonth;
  const savings = yearlySavingsPercent(price.basePackagePriceMonth, price.basePackagePriceYear);
  const currencySymbol = teamCountryCurrency(DEFAULT_TEAM_COUNTRY).symbol;
  const money = (value: number) => `${currencySymbol}${formatPrice(value)}`;

  const goCta = () => navigate(isAuthenticated ? '/orders' : '/register');

  return (
    <section id="pricing" className="px-5 py-10 bg-[#f8f9fa]">
      <div className="text-center mb-6">
        <span className="inline-block bg-green-50 text-green-600 text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full border border-green-100 mb-3">
          Pricing
        </span>
        <h2 className="text-gray-800 font-bold text-2xl leading-tight">
          Team seats, simply priced
        </h2>
        <p className="text-gray-500 text-sm mt-2 leading-relaxed">
          Base plan covers every included seat. Extra people are billed per seat.
          New teams get one month free — once per account.
        </p>
        {loading && (
          <div className="mt-3 flex items-center justify-center">
            <i className="ri-loader-4-line animate-spin text-green-500 text-lg" aria-hidden />
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-3 mb-5">
        <span className={`text-sm font-medium ${!annual ? 'text-gray-800' : 'text-gray-400'}`}>Monthly</span>
        <button
          type="button"
          onClick={() => setAnnual(!annual)}
          className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${annual ? 'bg-green-600' : 'bg-gray-200'}`}
          aria-label="Toggle annual billing"
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${annual ? 'translate-x-6' : 'translate-x-0'}`}
          />
        </button>
        <span className={`text-sm font-medium ${annual ? 'text-gray-800' : 'text-gray-400'}`}>
          Yearly
          {savings !== null && (
            <span className="ml-1.5 bg-green-100 text-green-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full border border-green-200">
              Save {savings}%
            </span>
          )}
        </span>
      </div>

      <div className="bg-white rounded-2xl border-2 border-green-500 overflow-hidden mb-4">
        <div className="bg-green-500 px-4 py-1.5 flex items-center justify-center gap-1.5">
          <i className="ri-gift-fill text-white text-xs" />
          <span className="text-white text-[11px] font-bold tracking-wide uppercase">
            1 month free when creating a new team
          </span>
        </div>

        <div className="p-5">
          <div className="flex items-start justify-between mb-1">
            <div>
              <p className="text-gray-800 font-bold text-base">Team Plan</p>
              <p className="text-gray-400 text-[11px] mt-0.5">
                Includes {included} seat{included === 1 ? '' : 's'} in the base fee
              </p>
            </div>
            <div className="text-right">
              <p className="text-gray-800 font-bold text-2xl leading-none">
                {money(baseDisplay)}
              </p>
              <p className="text-gray-400 text-[10px] mt-0.5">
                {annual ? '/year base' : '/mo base'}
              </p>
            </div>
          </div>

          <p className="text-green-700 text-xs font-medium bg-green-50 border border-green-100 rounded-lg px-3 py-2 mt-3">
            Extra seats {annual ? `${money(extraUnit)}/seat/year` : `${money(extraUnit)}/seat/mo`}
            {' · '}billed with your plan cycle
          </p>

          <div className="mt-4 bg-gray-50 rounded-xl px-3 py-3">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-gray-800 text-sm font-semibold">Team size</p>
                <p className="text-gray-400 text-[10px]">
                  {extraCount > 0
                    ? `${included} included + ${extraCount} extra`
                    : `All ${size} within the included limit`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setTeamSize(Math.max(1, size - 1))}
                  disabled={size <= 1}
                  className="w-8 h-8 rounded-lg bg-white border border-gray-200 text-gray-700 font-bold cursor-pointer disabled:opacity-40"
                  aria-label="Decrease team size"
                >
                  −
                </button>
                <span className="text-gray-900 font-bold text-lg w-6 text-center">{size}</span>
                <button
                  type="button"
                  onClick={() => setTeamSize(size + 1)}
                  className="w-8 h-8 rounded-lg bg-white border border-gray-200 text-gray-700 font-bold cursor-pointer"
                  aria-label="Increase team size"
                >
                  +
                </button>
              </div>
            </div>
            <div className="flex items-end justify-between border-t border-gray-200 pt-2">
              <span className="text-gray-500 text-xs">Estimated total</span>
              <span className="text-gray-900 font-bold text-xl leading-none">
                {money(displayAmount)}
                <span className="text-gray-400 text-[10px] font-medium">
                  {annual ? '/yr' : '/mo'}
                </span>
              </span>
            </div>
          </div>

          <ul className="mt-4 space-y-2">
            {[
              `Base fee covers all ${included} included seats`,
              'Add or remove members anytime',
              'Full access during free trial',
              'Upgrade anytime — remaining period is prorated',
            ].map((text) => (
              <li key={text} className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-green-500 text-sm mt-0.5 flex-shrink-0" />
                <span className="text-gray-600 text-xs leading-snug">{text}</span>
              </li>
            ))}
          </ul>

          <button
            type="button"
            onClick={goCta}
            className="w-full mt-5 py-3 rounded-xl text-sm font-semibold bg-green-600 text-white cursor-pointer hover:bg-green-700"
          >
            {isAuthenticated ? 'Go to workspace' : 'Start 1-month free trial'}
          </button>
          <p className="text-center text-gray-400 text-[10px] mt-2">
            {isAuthenticated
              ? 'Trial can only be claimed once when creating a new team.'
              : 'No long-term contract. Cancel anytime.'}
          </p>
        </div>
      </div>

      <div className="bg-green-600 rounded-2xl p-5 mb-5 text-center">
        <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center mx-auto mb-3">
          <i className="ri-leaf-line text-white text-xl" />
        </div>
        <p className="text-white font-bold text-base">
          {isAuthenticated ? 'Continue in your workspace' : 'Join the free trial'}
        </p>
        <p className="text-green-100 text-xs leading-relaxed mt-1.5">
          {isAuthenticated
            ? 'You are already signed in. The 1-month free trial is available once, only when creating a new team.'
            : 'Create a new team to get one full month of every plan feature — one trial per account.'}
        </p>
        <button
          type="button"
          onClick={goCta}
          className="mt-4 w-full py-3 rounded-xl text-sm font-semibold bg-white text-green-700 cursor-pointer"
        >
          {isAuthenticated ? 'Go to workspace' : 'Create a free account'}
        </button>
      </div>

      <div>
        <p className="text-gray-500 text-[10px] font-semibold uppercase tracking-widest mb-3 text-center">
          How billing works
        </p>
        <div className="space-y-2.5">
          {BILLING_POINTS.map((item) => (
            <div key={item.title} className="bg-white rounded-xl border border-gray-100 px-3 py-3 flex gap-3">
              <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-green-600 text-base`} />
              </div>
              <div className="min-w-0">
                <p className="text-gray-800 text-sm font-semibold leading-snug">{item.title}</p>
                <p className="text-gray-400 text-[11px] leading-relaxed mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
